import React from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Bell, Check, Building2, FileText, ShoppingBag, Users, ArrowLeft } from "lucide-react";
import { useNotifications, routeForNotification } from "../../context/NotificationContext";

const SECTION_ICON = {
  businesses: Building2,
  enquiries: FileText,
  orders: ShoppingBag,
  employees: Users,
};

function relTime(iso) {
  try {
    const t = new Date(iso).getTime();
    const s = Math.max(1, Math.round((Date.now() - t) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    return `${Math.round(s / 86400)}d ago`;
  } catch (_e) {
    return "";
  }
}

export function NotificationPanel() {
  const nav = useNavigate();
  const { list, panelOpen, setPanelOpen, markRead, markAllRead, counts } = useNotifications();

  const onTap = async (n) => {
    if (!n.read) await markRead(n.id);
    setPanelOpen(false);
    nav(routeForNotification(n));
  };

  return (
    <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
      <SheetContent
        side="right"
        data-testid="notif-panel"
        className="w-full sm:max-w-md p-0 flex flex-col"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100 flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {/* #4 — explicit Back: just closes the overlay, so whatever page
                was open underneath (Orders, Enquiries, ...) reappears exactly
                as it was, scroll position and all. */}
            <button
              type="button"
              onClick={() => setPanelOpen(false)}
              data-testid="notif-panel-back"
              className="shrink-0 -ml-1 w-8 h-8 rounded-full grid place-items-center text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition"
              aria-label="Back"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={2.4} />
            </button>
            <SheetTitle className="flex items-center gap-2 text-slate-900 min-w-0">
              <Bell className="w-5 h-5 text-emerald-600 shrink-0" strokeWidth={2.4} />
              <span className="truncate">Notifications</span>
              {counts.total > 0 && (
                <span className="ml-1 text-[11px] font-bold text-white bg-red-500 rounded-full px-1.5 py-0.5 tabular-nums shrink-0">
                  {counts.total}
                </span>
              )}
            </SheetTitle>
          </div>
          {counts.total > 0 && (
            <button
              type="button"
              onClick={() => markAllRead()}
              data-testid="notif-mark-all-read"
              className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 flex items-center gap-1 shrink-0"
            >
              <Check className="w-3.5 h-3.5" /> Mark all read
            </button>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto" data-testid="notif-list">
          {list.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">
              You&apos;re all caught up.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {list.map((n) => {
                const Icon = SECTION_ICON[n.section] || Bell;
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => onTap(n)}
                      data-testid={`notif-item-${n.id}`}
                      className={`w-full text-left flex gap-3 px-5 py-3 transition ${
                        n.read ? "bg-white" : "bg-emerald-50/60"
                      } hover:bg-slate-50`}
                    >
                      <div className={`w-9 h-9 rounded-2xl grid place-items-center shrink-0 ${
                        n.read ? "bg-slate-100 text-slate-500" : "bg-emerald-100 text-emerald-700"
                      }`}>
                        <Icon className="w-4 h-4" strokeWidth={2.4} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className={`text-sm leading-snug ${n.read ? "text-slate-700 font-medium" : "text-slate-900 font-semibold"}`}>
                          {n.title}
                        </div>
                        {n.body ? <div className="text-xs text-slate-500 mt-0.5 truncate">{n.body}</div> : null}
                        <div className="text-[10px] text-slate-400 mt-1">{relTime(n.created_at)}</div>
                      </div>
                      {!n.read && (
                        <span className="w-2 h-2 rounded-full bg-emerald-500 mt-2 shrink-0" aria-hidden />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
